import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { Check, Pencil, Pin, PinOff, SmilePlus, Trash2, X } from "lucide-react";
import { normalizeUserId, type ChatReaction, type ChatScope } from "@/api/connectApi";
import { useChatMessageAction } from "@/api/hooks";
import { fileMarkersOf, splitFileMarkers } from "@/lib/fileMarkers";
import { hasPollMarker } from "@/lib/pollMarkers";
import { cn } from "@/lib/utils";
import { MessageBody } from "./files/MessageBody";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🎉"];

/**
 * The body of one DM / group-chat message with Teams-style actions: a hover
 * bar (quick reactions, plus Edit and Delete on your own messages), inline
 * editing, an "(edited)" mark, and reaction chips that toggle on click.
 * Place it inside an element with the `group` class so the bar shows on hover.
 */
export function ChatMessageContent({
    scope,
    containerId,
    message,
    reactions,
    className,
    bubbleClassName,
    pin
}: {
    scope: ChatScope;
    containerId: string;
    message: { id: string; bodyHtml: string; authorUserId: string; isEdited?: boolean };
    reactions: ChatReaction[] | undefined;
    className?: string;
    /** Classes for the bubble around the text; reaction pills then sit under the bubble. */
    bubbleClassName?: string;
    /** Pinning (1:1 and group chats): whether it's pinned and how to toggle it. No pin action without it. */
    pin?: { pinned: boolean; onToggle: () => void };
}) {
    const auth = useAuth();
    const me = normalizeUserId(auth.user?.profile.sub);
    const mine = me !== "" && normalizeUserId(message.authorUserId) === me;
    // A poll message isn't edited — the poll card is the content.
    const editable = mine && !hasPollMarker(message.bodyHtml);
    const action = useChatMessageAction(scope, containerId);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [picking, setPicking] = useState(false);

    const chips = (reactions ?? []).filter((r) => r.messageId === message.id);
    const reactedByMe = (emoji: string) =>
        chips.some((c) => c.emoji === emoji && c.userIds.some((u) => normalizeUserId(u) === me));

    const toggle = (emoji: string) => {
        setPicking(false);
        action.mutate({ type: reactedByMe(emoji) ? "unreact" : "react", messageId: message.id, emoji });
    };

    const startEdit = () => {
        // Edit the text only; the message's files are carried over untouched.
        setDraft(htmlToText(splitFileMarkers(message.bodyHtml).html));
        setConfirmDelete(false);
        setEditing(true);
    };

    const saveEdit = () => {
        const text = draft.trim();
        const files = fileMarkersOf(message.bodyHtml);
        if (!text && !files) return;
        const body = text ? `<p>${escapeHtml(text).replace(/\n/g, "<br/>")}</p>` : "";
        action.mutate(
            { type: "edit", messageId: message.id, bodyHtml: body + files },
            { onSuccess: () => setEditing(false) }
        );
    };

    return (
        <div className={cn("relative", className)}>
            {/* The bubble holds the text (or the edit box); reactions sit under it. */}
            <div className={bubbleClassName}>
            {editing ? (
                <div className="mt-1 space-y-1">
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                            if (e.key === "Escape") setEditing(false);
                        }}
                        autoFocus
                        rows={2}
                        aria-label="Edit message"
                        className="w-full resize-y rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary"
                    />
                    <div className="flex justify-end gap-1.5">
                        <button type="button" onClick={() => setEditing(false)} className="h-7 rounded-md border px-2 text-xs hover:bg-accent">
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={saveEdit}
                            disabled={action.isPending || (!draft.trim() && !fileMarkersOf(message.bodyHtml))}
                            className="flex h-7 items-center gap-1 rounded-md bg-primary px-2 text-xs font-medium text-primary-foreground disabled:opacity-50"
                        >
                            <Check className="h-3.5 w-3.5" /> Save
                        </button>
                    </div>
                </div>
            ) : (
                <MessageBody
                    html={message.bodyHtml}
                    className={cn(!bubbleClassName && "mt-0.5", "break-words text-sm text-foreground [&_a]:text-primary [&_a]:underline")}
                />
            )}
            {(message.isEdited || pin?.pinned) && !editing ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                    {pin?.pinned ? <><Pin className="h-3 w-3 text-amber-500" aria-hidden />Pinned</> : null}
                    {message.isEdited && pin?.pinned ? " · " : null}
                    {message.isEdited ? "(edited)" : null}
                </span>
            ) : null}
            </div>

            {confirmDelete ? (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Delete this message for everyone?</span>
                    <button
                        type="button"
                        disabled={action.isPending}
                        onClick={() => action.mutate(
                            { type: "delete", messageId: message.id },
                            { onSuccess: () => setConfirmDelete(false) }
                        )}
                        className="rounded-md bg-destructive px-2 py-0.5 font-medium text-destructive-foreground disabled:opacity-50"
                    >
                        Delete
                    </button>
                    <button type="button" onClick={() => setConfirmDelete(false)} className="rounded-md border px-2 py-0.5 hover:bg-accent">
                        Cancel
                    </button>
                </div>
            ) : null}

            {chips.length > 0 ? (
                <div className={cn("mt-1 flex flex-wrap gap-1", mine && "justify-end")}>
                    {chips.map((r) => {
                        const minePressed = reactedByMe(r.emoji);
                        return (
                            <button
                                key={r.emoji}
                                type="button"
                                onClick={() => toggle(r.emoji)}
                                aria-pressed={minePressed}
                                title={`${r.count} ${r.count === 1 ? "person" : "people"} reacted with ${r.emoji}`}
                                className={cn(
                                    // The mock-up's reaction pill; yours takes the accent.
                                    "inline-flex items-center gap-1 rounded-full border px-[7px] py-px text-xs tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                    minePressed
                                        ? "border-primary bg-[var(--connect-primary-soft)] text-primary"
                                        : "border-[var(--connect-line)] bg-card hover:bg-[var(--connect-hover)]"
                                )}
                            >
                                <span>{r.emoji}</span>
                                <span className="font-medium">{r.count}</span>
                            </button>
                        );
                    })}
                </div>
            ) : null}

            {!editing ? (
                <div
                    role="toolbar"
                    aria-label="Message actions"
                    className="absolute -top-4 right-1 z-10 hidden items-center gap-0.5 rounded-md border bg-popover p-0.5 shadow-sm group-hover:flex group-focus-within:flex"
                >
                    {picking ? (
                        <>
                            {QUICK_REACTIONS.map((e) => (
                                <button
                                    key={e}
                                    type="button"
                                    onClick={() => toggle(e)}
                                    aria-label={`React with ${e}`}
                                    className="flex h-6 w-6 items-center justify-center rounded text-sm hover:bg-accent"
                                >
                                    {e}
                                </button>
                            ))}
                            <ToolbarButton label="Close reactions" onClick={() => setPicking(false)}>
                                <X className="h-3.5 w-3.5" />
                            </ToolbarButton>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => toggle("👍")}
                                aria-label="Like"
                                title="Like"
                                className="flex h-6 w-6 items-center justify-center rounded text-sm hover:bg-accent"
                            >
                                👍
                            </button>
                            <ToolbarButton label="More reactions" onClick={() => setPicking(true)}>
                                <SmilePlus className="h-3.5 w-3.5" />
                            </ToolbarButton>
                            {pin ? (
                                <ToolbarButton label={pin.pinned ? "Unpin message" : "Pin message"} onClick={pin.onToggle}>
                                    {pin.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                                </ToolbarButton>
                            ) : null}
                            {editable ? (
                                <ToolbarButton label="Edit message" onClick={startEdit}>
                                    <Pencil className="h-3.5 w-3.5" />
                                </ToolbarButton>
                            ) : null}
                            {mine ? (
                                <ToolbarButton label="Delete message" onClick={() => setConfirmDelete(true)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </ToolbarButton>
                            ) : null}
                        </>
                    )}
                </div>
            ) : null}
        </div>
    );
}

function ToolbarButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
        >
            {children}
        </button>
    );
}

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

/** Plain text of a message for the edit box: line breaks and block ends become newlines. */
function htmlToText(html: string): string {
    // DOMParser documents are inert — no scripts run and no images load.
    const withBreaks = html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li)>/gi, "\n");
    const doc = new DOMParser().parseFromString(withBreaks, "text/html");
    return (doc.body.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
}
